import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, ArrowRight, Shield, Search, Landmark, FileCheck, Building, UserCheck } from "lucide-react";
import { servicesData } from "@/lib/servicesData";

const iconMap = { Shield, Search, Landmark, FileCheck, Building, UserCheck };

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020408]" data-testid="service-not-found">
        <div className="text-center">
          <h1 className="font-outfit font-bold text-3xl text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-blue-400 hover:text-blue-300">
            &larr; Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Shield;

  return (
    <div className="relative min-h-screen bg-[#020408]" data-testid={`service-detail-${slug}`}>
      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background image with mask reveal */}
        <div className="absolute inset-0">
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover opacity-20"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#020408]/60 via-[#020408]/90 to-[#020408]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-12">
          {/* Back */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/services"
              data-testid="back-to-services"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors mb-10"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Services
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-blue-400" />
                  </div>
                  <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">
                    Certification Service
                  </span>
                </div>
                <h1 className="font-outfit font-bold text-4xl sm:text-5xl text-white tracking-tight">
                  {service.title}
                </h1>
                <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
                  {service.description}
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-8"
              >
                <Link
                  to="/contact"
                  data-testid="service-detail-cta"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3.5 rounded-md transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:-translate-y-0.5"
                >
                  Request This Certificate
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-[#0F1116]/60 backdrop-blur-xl border border-white/[0.06] rounded-xl p-8"
            >
              <h3 className="font-outfit font-semibold text-xl text-white mb-6">Key Benefits</h3>
              <div className="space-y-4">
                {service.benefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">Process</span>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">
              Certification Process
            </h2>
          </motion.div>

          <div className="relative max-w-3xl mx-auto">
            {/* Timeline line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/50 via-blue-500/20 to-transparent" />

            {service.process.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className={`relative flex items-start gap-6 mb-12 ${
                  i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                {/* Step number */}
                <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#0F1116] border-2 border-blue-500/30 flex items-center justify-center z-10">
                  <span className="font-outfit font-bold text-blue-400 text-sm">{step.step}</span>
                </div>

                {/* Content */}
                <div className={`ml-16 md:ml-0 md:w-[calc(50%-3rem)] ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                  <div className="bg-[#0F1116]/60 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-blue-500/20 transition-colors duration-300">
                    <h4 className="font-outfit font-semibold text-white text-lg">{step.title}</h4>
                    <p className="mt-2 text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit font-bold text-3xl text-white">
              Start Your {service.title} Today
            </h2>
            <p className="mt-4 text-slate-400">
              Our experts are ready to guide you through the certification process.
            </p>
            <Link
              to="/contact"
              data-testid="service-detail-bottom-cta"
              className="inline-block mt-8 bg-blue-600 hover:bg-blue-500 text-white font-medium px-10 py-3.5 rounded-md transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:-translate-y-0.5"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
